
import React, { useState, useEffect, useRef } from 'react';
import { X, Calendar, Clock, CheckCircle2, AlertCircle, MessageSquare, Send, User, Trash2, MoreHorizontal, ExternalLink, Briefcase, Flag, Paperclip, Loader2, FileText, Plus } from 'lucide-react';
import { supabase, supabase_upload_todo_attachment } from '../services/supabaseService';

interface TodoDetailModalProps {
    todoId: string;
    onClose: () => void;
    onUpdate?: () => void;
}

const TodoDetailModal: React.FC<TodoDetailModalProps> = ({ todoId, onClose, onUpdate }) => {
    const [todo, setTodo] = useState<any>(null);
    const [comments, setComments] = useState<any[]>([]);
    const [newComment, setNewComment] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSending, setIsSending] = useState(false);
    const [currentUser, setCurrentUser] = useState<any>(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [colleagues, setColleagues] = useState<any[]>([]);
    const [isAssigning, setIsAssigning] = useState(false);
    const [isUploadingAttach, setIsUploadingAttach] = useState(false);

    const commentsEndRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        loadData();
        checkUser();
    }, [todoId]);

    useEffect(() => {
        scrollToBottom();
    }, [comments]);

    const scrollToBottom = () => {
        commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const checkUser = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
            setCurrentUser(user);
            const { data: profile } = await supabase.from('profiles').select('role, client_id').eq('id', user.id).single();
            const adminCheck = profile?.role === 'ADMIN' || profile?.role === 'CLIENT';
            setIsAdmin(adminCheck);

            if (adminCheck && profile?.client_id) {
                const { data: team } = await supabase.from('profiles').select('id, name, email').eq('client_id', profile.client_id);
                setColleagues(team || []);
            }
        }
    };

    const loadData = async () => {
        setIsLoading(true);
        try {
            // Fetch Todo
            const { data: todoData, error: todoError } = await supabase
                .from('todos')
                .select(`
                    *,
                    vacancy:vacancy_id(title),
                    candidate:candidate_id(name)
                `)
                .eq('id', todoId)
                .single();

            if (todoError) throw todoError;

            // Fetch Comments
            const { data: commentsData, error: commentsError } = await supabase
                .from('todo_comments')
                .select('*')
                .eq('todo_id', todoId)
                .order('created_at', { ascending: true });

            if (commentsError) throw commentsError;

            // Fetch Profiles for names/emails
            const userIds = new Set<string>();
            if (todoData.user_id) userIds.add(todoData.user_id);
            if (todoData.assignee_id) userIds.add(todoData.assignee_id);
            commentsData?.forEach((c: any) => c.user_id && userIds.add(c.user_id));

            let profilesMap: Record<string, { email?: string; name?: string }> = {};
            if (userIds.size > 0) {
                const { data: profiles } = await supabase
                    .from('profiles')
                    .select('id, email, name')
                    .in('id', Array.from(userIds));

                profiles?.forEach((p: any) => {
                    profilesMap[p.id] = { email: p.email, name: p.name };
                });
            }

            // Map todo
            const enrichedTodo = {
                ...todoData,
                creator: profilesMap[todoData.user_id] || {},
                assignee: profilesMap[todoData.assignee_id] || {}
            };
            setTodo(enrichedTodo);

            // Map comments
            const enrichedComments = (commentsData || []).map((c: any) => ({
                ...c,
                user: profilesMap[c.user_id] || {}
            }));
            setComments(enrichedComments);

        } catch (e) {
            console.error("Error loading todo details", e);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSendComment = async () => {
        if (!newComment.trim() || !currentUser) return;
        setIsSending(true);
        try {
            const { data, error } = await supabase
                .from('todo_comments')
                .insert({
                    todo_id: todoId,
                    user_id: currentUser.id,
                    content: newComment
                })
                .select()
                .single();

            if (error) throw error;

            // Append with current user's profile info
            const newCommentObj = {
                ...data,
                user: { email: currentUser.email, name: currentUser.user_metadata?.name }
            };

            setComments([...comments, newCommentObj]);
            setNewComment('');
        } catch (e) {
            console.error("Error sending comment", e);
            alert("Kon reactie niet plaatsen.");
        } finally {
            setIsSending(false);
        }
    };

    const handleUploadAttachment = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        setIsUploadingAttach(true);
        try {
            const files = Array.from(e.target.files);
            const uploadPromises = files.map(file => supabase_upload_todo_attachment(file));
            const newUrls = await Promise.all(uploadPromises);

            const updatedAttachments = [...(todo.attachments || []), ...newUrls];
            
            // Update UI optimistically
            setTodo({ ...todo, attachments: updatedAttachments });

            // Update Database
            await supabase.from('todos').update({ attachments: updatedAttachments }).eq('id', todo.id);

            if (onUpdate) onUpdate();
        } catch (error) {
            console.error("Error uploading attachments:", error);
            alert("Er is een fout opgetreden bij het uploaden van de bijlage(n).");
        } finally {
            setIsUploadingAttach(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleToggleStatus = async () => {
        if (!todo) return;
        const newStatus = todo.status === 'Completed' ? 'Pending' : 'Completed';
        const { error } = await supabase
            .from('todos')
            .update({ status: newStatus, completed_at: newStatus === 'Completed' ? new Date().toISOString() : null })
            .eq('id', todoId);

        if (!error) {
            setTodo({ ...todo, status: newStatus });
            if (onUpdate) onUpdate();
        }
    };

    if (isLoading) {
        return (
            <div className="fixed inset-0 z-[400] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm">
                <div className="bg-white p-6 rounded-2xl shadow-xl">Loading...</div>
            </div>
        );
    }

    if (!todo) return null;

    return (
        <div className="fixed inset-0 z-[400] flex justify-end bg-slate-900/40 backdrop-blur-sm animate-fadeIn" onClick={onClose}>
            <div
                className="w-full max-w-lg bg-white h-full shadow-2xl flex flex-col animate-slideInRight"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-4 border-b border-slate-100 flex items-start justify-between bg-white relative z-10 shadow-sm">
                    <div className="flex items-start gap-3">
                        <button
                            onClick={handleToggleStatus}
                            className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all shrink-0 ${todo.status === 'Completed' ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 hover:border-indigo-500 text-transparent hover:text-indigo-200'}`}
                        >
                            <CheckCircle2 size={12} className={todo.status === 'Completed' ? 'text-white' : 'currentColor'} />
                        </button>
                        <div>
                            <h2 className={`text-base font-black text-slate-900 leading-snug ${todo.status === 'Completed' ? 'line-through text-slate-400' : ''}`}>{todo.title}</h2>
                            <div className="flex flex-wrap items-center gap-2 mt-2 text-[10px] font-bold text-slate-500 uppercase tracking-wide">
                                {todo.priority === 'High' && <span className="flex items-center gap-1 bg-amber-50 text-amber-600 border border-amber-200 px-1.5 py-0.5 rounded-md"><Flag size={10} /> Hoog</span>}
                                {todo.vacancy && <span className="flex items-center gap-1 bg-slate-50 border border-slate-200 text-slate-600 px-1.5 py-0.5 rounded-md"><Briefcase size={10} /> {todo.vacancy.title}</span>}
                                {todo.candidate && <span className="flex items-center gap-1 bg-slate-50 border border-slate-200 text-slate-600 px-1.5 py-0.5 rounded-md"><User size={10} /> {todo.candidate.name}</span>}
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"><X size={16} /></button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 custom-scrollbar bg-[#F8FAFC]">
                    <div className="space-y-4">
                        {/* Meta Data */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="p-3 bg-white rounded-xl border border-slate-200 shadow-sm">
                                <p className="text-[9px] uppercase tracking-widest text-slate-400 font-bold mb-1.5 flex items-center gap-1"><Calendar size={10} /> Deadline</p>
                                <div className="text-[13px] font-bold text-slate-700">
                                    {todo.due_date ? new Date(todo.due_date).toLocaleDateString() : 'Geen deadline'}
                                </div>
                            </div>
                            <div className="p-3 bg-white rounded-xl border border-slate-200 shadow-sm flex justify-between items-center group relative cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => isAdmin && setIsAssigning(!isAssigning)}>
                                <div>
                                    <p className="text-[9px] uppercase tracking-widest text-slate-400 font-bold mb-1.5 flex items-center gap-1"><User size={10} /> Toegewezen aan</p>
                                    <div className="flex items-center gap-2 text-[13px] font-bold text-slate-700">
                                        <div className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-[9px] font-black border border-indigo-200">{todo.assignee?.name?.charAt(0).toUpperCase() || todo.assignee?.email?.charAt(0).toUpperCase() || '?'}</div>
                                        {todo.assignee?.name?.split(' ')[0] || todo.assignee?.email?.split('@')[0] || 'Niemand'}
                                    </div>
                                </div>
                                {isAdmin && <MoreHorizontal size={14} className="text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />}

                                {isAssigning && (
                                    <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-slate-200 rounded-xl shadow-xl z-50 p-2 overflow-hidden animate-fadeIn" onClick={(e) => e.stopPropagation()}>
                                        <p className="text-[10px] font-bold text-slate-400 p-2 uppercase tracking-widest border-b border-slate-100">Kies recruiter</p>
                                        <div className="max-h-40 overflow-y-auto">
                                            {colleagues.map(c => (
                                                <button
                                                    key={c.id}
                                                    onClick={async () => {
                                                        setIsAssigning(false);
                                                        setTodo({ ...todo, assignee: { email: c.email } }); // Optimistic
                                                        await supabase.from('todos').update({ assignee_id: c.id }).eq('id', todo.id);
                                                        if (onUpdate) onUpdate();
                                                    }}
                                                    className="w-full text-left px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 transition-colors"
                                                >
                                                    {c.name || c.email}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {todo.quick_link_url && (
                            <div className="mt-2">
                                <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5 ml-1">Context Link</h3>
                                <a href={todo.quick_link_url} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white shadow-sm border border-slate-200 rounded-lg hover:border-indigo-300 hover:text-indigo-600 transition-colors text-[11px] font-bold text-slate-700 group">
                                    <ExternalLink size={12} className="text-slate-400 group-hover:text-indigo-600" />
                                    {todo.quick_link_title || 'Naar pagina...'}
                                </a>
                            </div>
                        )}

                        {todo.description && (
                            <div>
                                <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5 ml-1">Omschrijving</h3>
                                <p className="text-[13px] text-slate-600 bg-white p-3 rounded-xl border border-slate-200 shadow-sm whitespace-pre-wrap leading-relaxed">
                                    {todo.description}
                                </p>
                            </div>
                        )}

                        {/* Attachments Section */}
                        <div>
                            <div className="flex items-center justify-between mb-2 ml-1">
                                <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
                                    <Paperclip size={10} /> Bijlagen
                                </h3>
                                <div>
                                    <input 
                                        type="file" 
                                        multiple 
                                        ref={fileInputRef} 
                                        style={{ display: 'none' }} 
                                        onChange={handleUploadAttachment} 
                                    />
                                    <button 
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={isUploadingAttach}
                                        className="text-[10px] font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-2 py-1 rounded-md transition-colors flex items-center gap-1"
                                    >
                                        {isUploadingAttach ? <><Loader2 size={10} className="animate-spin" /> Uploaden...</> : <><Plus size={10} /> Toevoegen</>}
                                    </button>
                                </div>
                            </div>
                            
                            {todo.attachments && todo.attachments.length > 0 ? (
                                <div className="grid grid-cols-2 gap-2">
                                    {todo.attachments.map((url: string, idx: number) => {
                                        const isImage = url.match(/\.(jpeg|jpg|gif|png|webp)$/i) != null;
                                        const filename = url.split('/').pop()?.split('_').slice(1).join('_') || `Bijlage ${idx + 1}`;
                                        
                                        return (
                                            <a 
                                                key={idx} 
                                                href={url} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-2 p-2 bg-white border border-slate-200 shadow-sm rounded-lg hover:border-indigo-300 hover:shadow-md transition-all overflow-hidden group"
                                            >
                                                <div className="w-8 h-8 rounded shrink-0 bg-slate-50 flex items-center justify-center border border-slate-100 overflow-hidden">
                                                    {isImage ? (
                                                        <img src={url} alt={filename} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <FileText size={14} className="text-slate-400 group-hover:text-indigo-500 transition-colors" />
                                                    )}
                                                </div>
                                                <span className="text-[11px] font-medium text-slate-600 truncate group-hover:text-indigo-600 transition-colors">{filename}</span>
                                            </a>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-[11px] text-slate-400 italic px-1">Geen bijlagen.</div>
                            )}
                        </div>

                        <div className="h-px bg-slate-200 my-2"></div>

                        {/* Comments Section */}
                        <div>
                            <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-900 mb-3 flex items-center gap-1.5 ml-1">
                                <MessageSquare size={12} className="text-slate-400" /> Opmerkingen & Updates
                            </h3>

                            <div className="space-y-3 mb-20">
                                {comments.map(comment => (
                                    <div key={comment.id} className="flex gap-2.5">
                                        <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[9px] font-black text-slate-600 shadow-inner shrink-0 mt-0.5 border border-slate-300">
                                            {comment.user?.name?.charAt(0).toUpperCase() || comment.user?.email?.charAt(0).toUpperCase()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-baseline gap-2 mb-0.5">
                                                <span className="text-[11px] font-bold text-slate-900">{comment.user?.name?.split(' ')[0] || comment.user?.email?.split('@')[0]}</span>
                                                <span className="text-[9px] font-semibold text-slate-400">{new Date(comment.created_at).toLocaleString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                            </div>
                                            <div className="bg-white p-2.5 rounded-lg rounded-tl-none border border-slate-200 shadow-sm text-[13px] text-slate-700 whitespace-pre-wrap">
                                                {comment.content}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {comments.length === 0 && <p className="text-center text-slate-400 text-[11px] font-medium py-4">Nog geen opmerkingen aanwezig.</p>}
                                <div ref={commentsEndRef} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer Input */}
                <div className="p-3 border-t border-slate-200 bg-slate-50 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-20">
                    <div className="relative">
                        <input
                            type="text"
                            value={newComment}
                            onChange={e => setNewComment(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleSendComment()}
                            placeholder="Schrijf een reactie..."
                            className="w-full pl-3 pr-10 py-2.5 bg-white border border-slate-300 rounded-lg text-[13px] font-medium outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all placeholder:text-slate-400 shadow-inner"
                        />
                        <button
                            onClick={handleSendComment}
                            disabled={!newComment.trim() || isSending}
                            className="absolute right-1.5 top-1.5 p-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-sm"
                        >
                            <Send size={14} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TodoDetailModal;
